(function (root) {
  'use strict';

  const PRODUCT_IDS = Object.freeze({
    black: '15752050540892',
    chrome: '15752050606428'
  });

  const EXPECTED = Object.freeze({
    black: Object.freeze({
      '1000:standard': Object.freeze({ id: '58721072218460', sku: 'OPS-VD-BLACK-CAFE-BARRIER-7' }),
      '1500:standard': Object.freeze({ id: '58721072251228', sku: 'OPS-VD-BLACK-CAFE-BARRIER-8' }),
      '2000:standard': Object.freeze({ id: '58721072283996', sku: 'OPS-VD-BLACK-CAFE-BARRIER-9' }),
      '1000:heavy-duty': Object.freeze({ id: '58721072316764', sku: 'OPS-VD-BLACK-CAFE-BARRIER-10' }),
      '1500:heavy-duty': Object.freeze({ id: '58721072349532', sku: 'OPS-VD-BLACK-CAFE-BARRIER-11' }),
      '2000:heavy-duty': Object.freeze({ id: '58721072382300', sku: 'OPS-VD-BLACK-CAFE-BARRIER-12' })
    }),
    chrome: Object.freeze({
      '1000:standard': Object.freeze({ id: '58721084866908', sku: 'OPS-VD-CHROME-CAFE-BARRIER-7' }),
      '1500:standard': Object.freeze({ id: '58721084899676', sku: 'OPS-VD-CHROME-CAFE-BARRIER-8' }),
      '2000:standard': Object.freeze({ id: '58721084932444', sku: 'OPS-VD-CHROME-CAFE-BARRIER-9' }),
      '1000:heavy-duty': Object.freeze({ id: '58721084965212', sku: 'OPS-VD-CHROME-CAFE-BARRIER-10' }),
      '1500:heavy-duty': Object.freeze({ id: '58721084997980', sku: 'OPS-VD-CHROME-CAFE-BARRIER-11' }),
      '2000:heavy-duty': Object.freeze({ id: '58721085030748', sku: 'OPS-VD-CHROME-CAFE-BARRIER-12' })
    })
  });

  const LAYOUT_PANEL_COUNTS = Object.freeze({
    straight: 1,
    extension: 1,
    l_shaped: 2,
    c_shaped: 3
  });

  const LAYOUT_POST_COUNTS = Object.freeze({
    straight: 2,
    extension: 1,
    l_shaped: 3,
    c_shaped: 4,
    replacement_graphics: 0
  });

  const WIDTHS = new Set([1000, 1500, 2000]);
  const ATTEMPT_KEY_PREFIX = 'ops-cafe-barrier-cart-attempt:';
  const MAX_ARTWORK_BYTES = 25 * 1024 * 1024;
  const ALLOWED_ARTWORK_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml', 'application/pdf']);

  function panelCountForLayout(layout, replacementCount = 1) {
    if (layout === 'replacement_graphics') {
      const count = Number(replacementCount);
      return Number.isInteger(count) && count >= 1 && count <= 3 ? count : 1;
    }
    return LAYOUT_PANEL_COUNTS[layout] || 0;
  }

  function validateArtworkFile(file) {
    if (!file || typeof file.size !== 'number' || file.size < 1) return 'missing';
    if (file.size > MAX_ARTWORK_BYTES) return 'size';
    if (!ALLOWED_ARTWORK_TYPES.has(String(file.type || '').toLowerCase())) return 'type';
    return '';
  }

  function normalizePanel(panel, index) {
    if (!panel || typeof panel !== 'object') return null;
    const widthMm = Number(panel.widthMm);
    const sidedness = panel.sidedness === 'single' ? 'single' : panel.sidedness === 'double' ? 'double' : '';
    const fixing = panel.fixing === 'spring_clip' ? 'spring_clip' : panel.fixing === 'rail_pocket' ? 'rail_pocket' : '';
    if (!WIDTHS.has(widthMm) || !sidedness || !fixing) return null;
    return Object.freeze({
      panelId: String.fromCharCode(65 + index),
      widthMm,
      sidedness,
      fixing,
      sameArtworkBothSides: sidedness === 'double' ? panel.sameArtworkBothSides !== false : false,
      frontFile: panel.frontFile || null,
      backFile: panel.backFile || null
    });
  }

  function buildDraftConfiguration(input) {
    if (!input || typeof input !== 'object') return null;
    const layout = String(input.layout || '');
    if (!Object.hasOwn(LAYOUT_POST_COUNTS, layout)) return null;
    const finish = String(input.finish || '');
    if (!Object.hasOwn(PRODUCT_IDS, finish)) return null;
    const expectedPanels = panelCountForLayout(layout, input.replacementCount);
    if (!Array.isArray(input.panels) || input.panels.length !== expectedPanels) return null;
    const panels = input.panels.map(normalizePanel);
    if (panels.some((panel) => !panel)) return null;
    const base = layout === 'replacement_graphics' ? 'standard' : String(input.base || '');
    if (!['standard', 'heavy-duty'].includes(base)) return null;
    const existingCompatiblePostConfirmed = layout === 'extension' ? input.existingCompatiblePostConfirmed === true : false;
    const existingSystemReference = layout === 'extension' ? String(input.existingSystemReference || '').trim() : '';
    if (layout === 'extension' && (!existingCompatiblePostConfirmed || !existingSystemReference || existingSystemReference.length > 256)) return null;
    return Object.freeze({
      layout,
      finish,
      base,
      replacementCount: expectedPanels,
      existingCompatiblePostConfirmed,
      existingSystemReference,
      panels: Object.freeze(panels)
    });
  }

  function hardwareSummary(draft) {
    if (!draft) return Object.freeze({ posts: 0, rails: 0, clipPairs: 0, graphics: 0 });
    if (draft.layout === 'replacement_graphics') {
      return Object.freeze({ posts: 0, rails: 0, clipPairs: 0, graphics: draft.panels.length });
    }
    let rails = 0;
    let clipPairs = 0;
    for (const panel of draft.panels) {
      if (panel.fixing === 'rail_pocket') rails += 2;
      else {
        rails += 1;
        clipPairs += 1;
      }
    }
    return Object.freeze({
      posts: LAYOUT_POST_COUNTS[draft.layout] || 0,
      rails,
      clipPairs,
      graphics: draft.panels.length
    });
  }


  function configurationSummary(draft) {
    if (!draft) return '';
    const layoutLabels = {
      straight: 'Straight',
      extension: 'Extension',
      l_shaped: 'L shape',
      c_shaped: 'C shape',
      replacement_graphics: 'Replacement graphics only'
    };
    const lines = [
      'Café barrier setup',
      'Layout: ' + (layoutLabels[draft.layout] || draft.layout),
      'Finish: ' + (draft.finish === 'chrome' ? 'Chrome' : 'Black')
    ];
    if (draft.layout !== 'replacement_graphics') {
      lines.push('Post/base: ' + (draft.base === 'heavy-duty' ? 'Heavy duty' : 'Standard'));
    }
    if (draft.layout === 'extension') {
      lines.push('Existing system reference: ' + draft.existingSystemReference);
    }
    draft.panels.forEach(function (panel) {
      const sides = panel.sidedness === 'double' ? 'Double-sided' : 'Single-sided';
      const fixing = panel.fixing === 'spring_clip' ? 'Spring clip' : 'Rail pocket';
      const artwork = panel.sidedness === 'double'
        ? panel.sameArtworkBothSides ? 'same artwork both sides' : 'separate front/back artwork'
        : 'front artwork only';
      lines.push('Panel ' + panel.panelId + ': ' + panel.widthMm + ' mm · ' + sides + ' · ' + fixing + ' · ' + artwork);
    });
    return lines.join('\n');
  }

  function resolveConfiguration(input) {
    if (!input || input.layout !== 'straight' || input.sides !== 'double-sided' || input.fixing !== 'rail_pocket') return null;
    const finish = String(input.finish || '');
    const width = String(input.width || '');
    const base = String(input.base || '');
    const slot = width + ':' + base;
    const expected = EXPECTED[finish] && EXPECTED[finish][slot];
    if (!expected) return null;
    if (String(input.productId || '') !== PRODUCT_IDS[finish]) return null;
    if (String(input.variantId || '') !== expected.id || String(input.sku || '') !== expected.sku) return null;
    if (input.available !== true) return null;
    if (typeof input.priceDisplay !== 'string' || input.priceDisplay.trim() === '') return null;
    return Object.freeze({
      finish,
      width,
      base,
      layout: 'straight',
      sides: 'double-sided',
      fixing: 'rail_pocket',
      productId: PRODUCT_IDS[finish],
      productTitle: String(input.productTitle || ''),
      productHandle: String(input.productHandle || finish + '-cafe-barrier'),
      variantId: expected.id,
      variantTitle: String(input.variantTitle || ''),
      sku: expected.sku,
      priceDisplay: input.priceDisplay
    });
  }

  function fixedCheckoutConfiguration(draft, variantSource) {
    if (!draft || draft.layout !== 'straight' || draft.panels.length !== 1) return null;
    const panel = draft.panels[0];
    if (panel.sidedness !== 'double' || panel.fixing !== 'rail_pocket') return null;
    const slot = String(panel.widthMm) + ':' + draft.base;
    const option = Array.from(variantSource?.options || []).find(function (candidate) {
      return candidate.dataset.finish === draft.finish && candidate.dataset.slot === slot;
    });
    if (!option) return null;
    return resolveConfiguration({
      finish: draft.finish,
      width: String(panel.widthMm),
      base: draft.base,
      layout: 'straight',
      sides: 'double-sided',
      fixing: 'rail_pocket',
      productId: option.dataset.productId,
      productTitle: option.dataset.productTitle,
      productHandle: option.dataset.productHandle,
      variantId: option.value,
      variantTitle: option.dataset.title,
      sku: option.dataset.sku,
      available: String(option.dataset.available) === 'true',
      priceDisplay: option.dataset.price
    });
  }

  function resolveCheckoutConfiguration(draft, fixedSource, modularSource) {
    const fixed = fixedCheckoutConfiguration(draft, fixedSource);
    if (fixed) return fixed;
    return root.OpsCafeBarrierComponentCart?.createPlan?.(draft, modularSource) || null;
  }

  function artworkReady(draft) {
    if (!draft) return false;
    return draft.panels.every(function (panel) {
      if (validateArtworkFile(panel.frontFile)) return false;
      if (panel.sidedness === 'single') return true;
      if (panel.sameArtworkBothSides) return true;
      return !validateArtworkFile(panel.backFile);
    });
  }

  function artworkFingerprint(draft) {
    if (!draft) return '';
    const parts = [];
    for (const panel of draft.panels) {
      const push = function (side, file) {
        if (!file) return;
        parts.push([
          panel.panelId,
          side,
          file.name || '',
          file.size || 0,
          file.lastModified || 0,
          file.type || ''
        ].map(encodeURIComponent).join(':'));
      };
      push('front', panel.frontFile);
      if (panel.sidedness === 'double' && !panel.sameArtworkBothSides) push('back', panel.backFile);
    }
    return parts.join('|');
  }

  function buildBomSnapshot(draft, configurationId, artworkRefs, shopify = null) {
    if (!draft || typeof configurationId !== 'string' || !configurationId) throw new Error('A valid café barrier draft is required.');
    if (!Array.isArray(artworkRefs) || artworkRefs.length !== draft.panels.length) throw new Error('Artwork references do not match the panel count.');
    const defaultFixing = draft.panels[0]?.fixing || 'rail_pocket';
    const snapshot = {
      schemaVersion: 1,
      configurationId,
      product: {
        family: 'cafe_barrier',
        frameFinish: draft.finish,
        baseType: draft.base === 'heavy-duty' ? 'heavy_duty' : 'standard',
        fixing: defaultFixing,
        layout: draft.layout
      },
      panels: draft.panels.map(function (panel, index) {
        const refs = artworkRefs[index];
        const artwork = { frontRef: refs.frontRef };
        if (panel.sidedness === 'double') artwork.backRef = refs.backRef;
        return {
          panelId: panel.panelId,
          widthMm: panel.widthMm,
          sidedness: panel.sidedness,
          fixing: panel.fixing,
          artwork
        };
      }),
      quantity: 1
    };
    if (draft.layout === 'extension') {
      snapshot.existingCompatiblePostConfirmed = true;
      snapshot.existingSystemReference = draft.existingSystemReference;
    }
    if (shopify) {
      snapshot.shopify = {
        productId: shopify.productId,
        variantId: shopify.variantId,
        sku: shopify.sku
      };
    }
    return snapshot;
  }

  function buildCartItem(configuration, snapshot, draft) {
    if (!configuration || !snapshot || snapshot.immutable !== true || snapshot.sessionId !== snapshot.finalizedSessionId) throw new Error('');
    if (!snapshot.configurationId || !snapshot.uploadReference || !snapshot.zipUrl || !snapshot.previewUrl || !snapshot.artworkFingerprint) throw new Error('');
    const expected = EXPECTED[configuration.finish]?.[configuration.width + ':' + configuration.base];
    if (!expected || expected.id !== configuration.variantId || expected.sku !== configuration.sku) throw new Error('');
    const panel = draft.panels[0];
    return {
      id: Number(expected.id),
      quantity: 1,
      properties: {
        '_ops_cafe_barrier_fixed_v1': configuration.finish + ':' + configuration.width + ':' + configuration.base + ':straight:double-sided',
        '_ops_cafe_barrier_configuration_id': snapshot.configurationId,
        '_upload_session_id': snapshot.sessionId,
        '_upload_reference': snapshot.uploadReference,
        '_Artwork files': snapshot.zipUrl,
        '_Artwork preview': snapshot.previewUrl,
        'Layout': 'Straight',
        'Frame finish': configuration.finish === 'black' ? 'Black' : 'Chrome',
        'Panel width': configuration.width + ' mm',
        'Post and base': configuration.base === 'heavy-duty' ? 'Heavy duty' : 'Standard',
        'Fixing': 'Rail pocket',
        'Printed sides': 'Double-sided',
        'Artwork sides': panel.sameArtworkBothSides ? 'Same artwork both sides' : 'Separate front and back artwork'
      }
    };
  }

  function reliableSessionStorage() {
    let storage;
    try { storage = root.sessionStorage; } catch (error) { return null; }
    if (!storage) return null;
    const key = ATTEMPT_KEY_PREFIX + 'probe';
    const value = String(Date.now()) + ':' + String(Math.random());
    try {
      storage.setItem(key, value);
      const ok = storage.getItem(key) === value;
      if (typeof storage.removeItem === 'function') storage.removeItem(key);
      return ok ? storage : null;
    } catch (error) {
      return null;
    }
  }

  function cartAttemptKey(configuration, fingerprint) {
    const key = configuration.kind === 'modular'
      ? configuration.planKey
      : configuration.finish + ':' + configuration.width + ':' + configuration.base;
    return ATTEMPT_KEY_PREFIX + encodeURIComponent(key) + ':' + encodeURIComponent(fingerprint || '');
  }

  function cartAddGuardState(configuration, fingerprint) {
    if (!configuration || !fingerprint) return Object.freeze({ canAdd: false, reason: 'unavailable' });
    const storage = reliableSessionStorage();
    if (!storage) return Object.freeze({ canAdd: false, reason: 'unavailable' });
    const key = cartAttemptKey(configuration, fingerprint);
    try {
      const marker = storage.getItem(key);
      if (marker === 'sent') return Object.freeze({ canAdd: false, reason: 'attempted' });
      if (marker !== null) return Object.freeze({ canAdd: false, reason: 'unavailable' });
      return Object.freeze({ canAdd: true, reason: '', storage, key });
    } catch (error) {
      return Object.freeze({ canAdd: false, reason: 'unavailable' });
    }
  }

  function canRetryCartAdd(configuration, fingerprint) {
    return cartAddGuardState(configuration, fingerprint).canAdd;
  }

  function reserveCartAdd(configuration, fingerprint) {
    const state = cartAddGuardState(configuration, fingerprint);
    if (!state.canAdd) return state;
    try {
      state.storage.setItem(state.key, 'sent');
      if (state.storage.getItem(state.key) !== 'sent') return Object.freeze({ canAdd: false, reason: 'unavailable' });
      return Object.freeze({ canAdd: true, reason: '', storage: state.storage, key: state.key });
    } catch (error) {
      return Object.freeze({ canAdd: false, reason: 'unavailable' });
    }
  }

  function rootPath() {
    const candidate = root.Shopify && root.Shopify.routes && root.Shopify.routes.root;
    return candidate && String(candidate).endsWith('/') ? String(candidate) : '/';
  }

  function secureId(prefix) {
    if (!root.crypto || typeof root.crypto.randomUUID !== 'function') throw new Error('Secure artwork snapshot IDs are unavailable in this browser.');
    return prefix + '_' + root.crypto.randomUUID().replace(/-/g, '');
  }

  function r2Reference(result) {
    const key = String(result?.objectKey || '');
    if (!key.startsWith('occult-print-shop-uploads/')) throw new Error('The artwork service returned an invalid object key.');
    return 'r2://occult-print-uploads/' + key;
  }

  function protectedWorkerUrl(workerUrl, value, expectedPath) {
    let parsed;
    let base;
    try {
      parsed = new URL(value);
      base = new URL(workerUrl);
    } catch (error) {
      throw new Error('The artwork service returned an invalid protected link.');
    }
    if (parsed.origin !== base.origin || parsed.pathname !== expectedPath || !parsed.searchParams.get('token')) {
      throw new Error('The artwork service returned an unexpected protected link.');
    }
    return parsed.toString();
  }

  async function uploadProductionFile(workerUrl, file, role, sessionId, configuration, metadata) {
    if (!workerUrl) throw new Error('The OPS artwork service is not configured.');
    const formData = new root.FormData();
    formData.append('file', file, file.name || role + '.bin');
    formData.append('sessionId', sessionId);
    formData.append('fileRole', role);
    formData.append('productTitle', configuration.productTitle || 'Café barrier');
    formData.append('productHandle', configuration.productHandle || configuration.finish + '-cafe-barrier');
    formData.append('variantTitle', configuration.variantTitle || configuration.sku);
    formData.append('previewType', 'cafe-barrier');
    if (metadata?.surfaceName) formData.append('surfaceName', metadata.surfaceName);
    if (metadata?.surfaceIndex !== undefined) formData.append('surfaceIndex', String(metadata.surfaceIndex));
    if (metadata?.selectedOptions) formData.append('selectedOptions', JSON.stringify(metadata.selectedOptions));
    const response = await root.fetch(String(workerUrl).replace(/\/+$/, '') + '/upload', { method: 'POST', body: formData });
    const text = await response.text();
    let result;
    try { result = text ? JSON.parse(text) : null; } catch (error) { result = null; }
    if (!response.ok || !result || result.ok !== true || result.sessionId !== sessionId || !result.objectKey || !result.finalizeUrl || !result.zipUrl || !result.previewUrl || !result.fileUrl) {
      throw new Error(result?.error || 'Artwork upload failed.');
    }
    return result;
  }

  async function drawArtworkThumb(context, file, x, y, width, height, label) {
    context.save();
    context.strokeStyle = '#c8c8c8';
    context.lineWidth = 2;
    context.strokeRect(x, y, width, height);
    context.fillStyle = '#111111';
    context.font = '700 24px sans-serif';
    context.fillText(label, x + 20, y + 34);
    if (file && file.type !== 'application/pdf' && typeof root.createImageBitmap === 'function') {
      try {
        const bitmap = await root.createImageBitmap(file);
        const pad = 20;
        const top = 54;
        const boxW = width - pad * 2;
        const boxH = height - top - 52;
        const scale = Math.min(boxW / bitmap.width, boxH / bitmap.height);
        const drawW = bitmap.width * scale;
        const drawH = bitmap.height * scale;
        context.drawImage(bitmap, x + pad + (boxW - drawW) / 2, y + top + (boxH - drawH) / 2, drawW, drawH);
        if (typeof bitmap.close === 'function') bitmap.close();
      } catch (error) {}
    }
    context.fillStyle = '#333333';
    context.font = '18px sans-serif';
    context.fillText(String(file?.name || 'Artwork').slice(0, 46), x + 20, y + height - 20);
    context.restore();
  }

  async function browserPreviewBlob(draft) {
    if (!root.document?.createElement) throw new Error('Artwork preview could not be created.');
    const canvas = root.document.createElement('canvas');
    const rowHeight = 360;
    canvas.width = 1200;
    canvas.height = 140 + draft.panels.length * rowHeight;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Artwork preview could not be created.');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#111111';
    context.font = '700 42px sans-serif';
    context.fillText('Café barrier artwork', 48, 58);
    context.font = '22px sans-serif';
    context.fillText(draft.finish + ' · ' + draft.layout.replaceAll('_', ' '), 48, 98);
    for (let index = 0; index < draft.panels.length; index += 1) {
      const panel = draft.panels[index];
      const y = 130 + index * rowHeight;
      await drawArtworkThumb(context, panel.frontFile, 48, y, 520, 320, 'Panel ' + panel.panelId + ' front');
      const backFile = panel.sidedness === 'double' ? (panel.sameArtworkBothSides ? panel.frontFile : panel.backFile) : null;
      if (backFile) await drawArtworkThumb(context, backFile, 632, y, 520, 320, 'Panel ' + panel.panelId + ' back');
      else {
        context.fillStyle = '#f5f5f5';
        context.fillRect(632, y, 520, 320);
        context.fillStyle = '#555555';
        context.font = '22px sans-serif';
        context.fillText('Single-sided panel', 660, y + 60);
      }
    }
    return await new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (blob) resolve(blob);
        else reject(new Error('Artwork preview could not be created.'));
      }, 'image/png');
    });
  }

  async function createProductionSnapshot(draft, configuration, workerUrl, messages = {}) {
    if (!draft || !configuration || !artworkReady(draft)) throw new Error(messages.artworkRequired || 'Add the required artwork.');
    const sessionId = secureId('cafe');
    const configurationId = secureId('cb');
    const artworkRefs = [];
    let firstUpload = null;
    let surfaceIndex = 0;
    const setStatus = typeof messages.setStatus === 'function' ? messages.setStatus : function () {};

    for (const panel of draft.panels) {
      setStatus((messages.uploadingArtwork || 'Uploading artwork…') + ' ' + panel.panelId);
      const selectedOptions = [
        { name: 'Layout', value: draft.layout },
        { name: 'Frame finish', value: draft.finish },
        { name: 'Base', value: draft.base },
        { name: 'Panel', value: panel.panelId },
        { name: 'Width', value: panel.widthMm + ' mm' },
        { name: 'Printed sides', value: panel.sidedness },
        { name: 'Fixing', value: panel.fixing }
      ];
      const front = await uploadProductionFile(workerUrl, panel.frontFile, 'original', sessionId, configuration, {
        surfaceName: 'Panel ' + panel.panelId + ' front',
        surfaceIndex: surfaceIndex++,
        selectedOptions
      });
      if (!firstUpload) firstUpload = front;
      let back = front;
      if (panel.sidedness === 'double' && !panel.sameArtworkBothSides) {
        back = await uploadProductionFile(workerUrl, panel.backFile, 'original', sessionId, configuration, {
          surfaceName: 'Panel ' + panel.panelId + ' back',
          surfaceIndex: surfaceIndex++,
          selectedOptions
        });
      }
      artworkRefs.push({
        frontRef: r2Reference(front),
        backRef: panel.sidedness === 'double' ? r2Reference(back) : null
      });
    }

    const project = buildBomSnapshot(
      draft, configurationId, artworkRefs, configuration.kind === 'modular' ? null : configuration
    );
    if (configuration.kind === 'modular') {
      project.checkout = {
        type: 'component-cart-v2',
        sku: configuration.sku,
        componentLines: configuration.lines.map((line) => ({
          role: line.role,
          panelId: line.panelId,
          variantId: String(line.id),
          productId: line.productId,
          sku: line.sku,
          quantity: line.quantity
        }))
      };
    }
    setStatus(messages.savingConfiguration || 'Saving configuration…');
    const projectFile = new root.File([JSON.stringify(project, null, 2)], 'cafe-barrier-configuration.json', {
      type: 'application/json',
      lastModified: Date.now()
    });
    const projectUpload = await uploadProductionFile(workerUrl, projectFile, 'project-data', sessionId, configuration, {
      surfaceName: 'Café barrier configuration',
      surfaceIndex: surfaceIndex++,
      selectedOptions: [{ name: 'Configuration ID', value: configurationId }]
    });

    setStatus(messages.creatingPreview || 'Creating artwork preview…');
    const previewFactory = typeof messages.previewBlobFactory === 'function' ? messages.previewBlobFactory : browserPreviewBlob;
    const previewBlob = await previewFactory(draft);
    const previewFile = new root.File([previewBlob], 'cafe-barrier-artwork-preview.png', {
      type: 'image/png',
      lastModified: Date.now()
    });
    const previewUpload = await uploadProductionFile(workerUrl, previewFile, 'customer-preview', sessionId, configuration, {
      surfaceName: 'Customer artwork preview',
      surfaceIndex: surfaceIndex,
      selectedOptions: [{ name: 'Configuration ID', value: configurationId }]
    });

    const encodedSession = encodeURIComponent(sessionId);
    const finalizeUrl = protectedWorkerUrl(workerUrl, previewUpload.finalizeUrl, '/finalize/' + encodedSession);
    const zipUrl = protectedWorkerUrl(workerUrl, previewUpload.zipUrl, '/zip/' + encodedSession);
    const previewUrl = protectedWorkerUrl(workerUrl, previewUpload.previewUrl, '/preview/' + encodedSession);
    const projectDataUrl = protectedWorkerUrl(workerUrl, projectUpload.fileUrl, '/file/' + encodeURIComponent(projectUpload.objectKey));

    setStatus(messages.finalizing || 'Locking artwork package…');
    const response = await root.fetch(finalizeUrl, { method: 'POST', headers: { Accept: 'application/json' } });
    const text = await response.text();
    let finalized;
    try { finalized = text ? JSON.parse(text) : null; } catch (error) { finalized = null; }
    if (!response.ok || finalized?.ok !== true || finalized?.immutable !== true || finalized?.sessionId !== sessionId) {
      throw new Error(finalized?.error || messages.finalizeFailed || 'The artwork package could not be locked safely.');
    }

    return Object.freeze({
      immutable: true,
      finalizedSessionId: finalized.sessionId,
      sessionId,
      configurationId,
      uploadReference: String(finalized.uploadReference || previewUpload.uploadReference || projectUpload.uploadReference || firstUpload?.uploadReference || ''),
      zipUrl,
      previewUrl,
      artworkFingerprint: artworkFingerprint(draft)
    });
  }

  async function addCartItem(configuration, snapshot, draft, messages = {}) {
    const isModular = configuration.kind === 'modular';
    const payload = { items: isModular
      ? root.OpsCafeBarrierComponentCart.buildCartItems(configuration, snapshot, draft)
      : [buildCartItem(configuration, snapshot, draft)] };
    const reservation = reserveCartAdd(configuration, snapshot.artworkFingerprint);
    if (!reservation.canAdd) {
      const error = new Error(reservation.reason === 'attempted' ? (messages.cartMayContainItem || '') : (messages.cartGuardUnavailable || ''));
      if (reservation.reason === 'attempted') error.cartMayContainItem = true;
      else error.cartGuardUnavailable = true;
      throw error;
    }

    const base = rootPath();
    try {
      const response = await root.fetch(base + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const added = await response.json();
      if (!response.ok) throw new Error(added.description || added.message || '');
      const addedLines = Array.isArray(added.items) ? added.items : [added];
      const expectedSession = snapshot.sessionId;
      const addedValid = isModular
        ? root.OpsCafeBarrierComponentCart.matchesCart(payload.items, addedLines, expectedSession, snapshot.configurationId)
        : addedLines.some((line) => line &&
            String(line.variant_id || line.id || '') === String(configuration.variantId) &&
            line.properties?._upload_session_id === expectedSession &&
            line.properties?._ops_cafe_barrier_configuration_id === snapshot.configurationId);
      if (!addedValid) throw new Error(messages.cartMismatch || '');

      const cartResponse = await root.fetch(base + 'cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      const persisted = isModular
        ? root.OpsCafeBarrierComponentCart.matchesCart(payload.items, cart.items, expectedSession, snapshot.configurationId)
        : Array.isArray(cart.items) && cart.items.some(function (item) {
          return String(item.variant_id || item.id || '') === String(configuration.variantId) &&
            item.properties?._upload_session_id === expectedSession &&
            item.properties?._ops_cafe_barrier_configuration_id === snapshot.configurationId;
        });
      if (!cartResponse.ok || !persisted) throw new Error(messages.cartPersistenceError || '');
      if (typeof messages.beforeNavigate === 'function') messages.beforeNavigate();
      root.location.assign(base + 'cart');
    } catch (error) {
      const failure = error instanceof Error ? error : new Error('');
      failure.cartMayContainItem = true;
      throw failure;
    }
  }

  function syncPanelCard(card) {
    const sides = card.querySelector('[data-panel-sides]');
    const same = card.querySelector('[data-panel-same]');
    const sameWrap = card.querySelector('[data-panel-same-wrap]');
    const backWrap = card.querySelector('[data-panel-back-wrap]');
    const back = card.querySelector('[data-panel-back]');
    const isDouble = sides?.value === 'double';
    if (sameWrap) sameWrap.hidden = !isDouble;
    if (backWrap) backWrap.hidden = !isDouble || Boolean(same?.checked);
    if (back) back.required = isDouble && !same?.checked;
  }

  function collectPanels(host) {
    return Array.from(host.querySelectorAll('[data-panel-card]')).map(function (card) {
      const sides = card.querySelector('[data-panel-sides]')?.value;
      const same = card.querySelector('[data-panel-same]')?.checked !== false;
      return {
        widthMm: Number(card.querySelector('[data-panel-width]')?.value || 0),
        sidedness: sides,
        fixing: card.querySelector('[data-panel-fixing]')?.value,
        sameArtworkBothSides: same,
        frontFile: card.querySelector('[data-panel-front]')?.files?.[0] || null,
        backFile: card.querySelector('[data-panel-back]')?.files?.[0] || null
      };
    });
  }

  // Keep actual file-input DOM nodes alive when switching between Straight/L/C.
  // Reconstructing file inputs would silently discard the customer's selected files.
  const panelCardCache = new WeakMap();

  function renderPanels(section, count) {
    const host = section.querySelector('[data-panels]');
    const template = section.querySelector('[data-panel-template]');
    if (!host || !template) return;
    let cards = panelCardCache.get(section);
    if (!cards) {
      cards = new Map();
      panelCardCache.set(section, cards);
    }
    for (const card of host.querySelectorAll('[data-panel-card]')) {
      const number = Number(card.dataset.panelIndex);
      if (Number.isInteger(number) && number >= 0) cards.set(number, card);
    }
    host.replaceChildren();
    for (let index = 0; index < count; index += 1) {
      let card = cards.get(index);
      if (!card) {
        const fragment = template.content.cloneNode(true);
        card = fragment.querySelector('[data-panel-card]');
        const width = card.querySelector('[data-panel-width]');
        const sides = card.querySelector('[data-panel-sides]');
        const fixing = card.querySelector('[data-panel-fixing]');
        if (width) width.value = '1000';
        if (sides) sides.value = 'double';
        if (fixing) fixing.value = 'rail_pocket';
        cards.set(index, card);
      }
      const panelId = String.fromCharCode(65 + index);
      card.dataset.panelIndex = String(index);
      card.dataset.panelId = panelId;
      const title = card.querySelector('[data-panel-title]');
      if (title) title.textContent = (section.dataset.panelLabel || 'Panel') + ' ' + panelId;
      host.appendChild(card);
      syncPanelCard(card);
    }
  }

  function layoutFromForm(form) {
    return form.querySelector('input[name="layout"]:checked')?.value || 'straight';
  }

  function draftFromForm(section) {
    const form = section.querySelector('[data-ops-cafe-barrier-form]');
    const layout = layoutFromForm(form);
    return buildDraftConfiguration({
      layout,
      finish: layout === 'replacement_graphics' ? 'black' : section.querySelector('[data-finish]')?.value,
      base: section.querySelector('[data-base]')?.value,
      replacementCount: Number(section.querySelector('[data-replacement-count]')?.value || 1),
      existingCompatiblePostConfirmed: Boolean(section.querySelector('[data-extension-confirm]')?.checked),
      existingSystemReference: section.querySelector('[data-extension-reference]')?.value || '',
      panels: collectPanels(section.querySelector('[data-panels]'))
    });
  }

  function setBusy(section, active, message) {
    const overlay = section.querySelector('[data-busy]');
    const text = section.querySelector('[data-busy-message]');
    if (text && message) text.textContent = message;
    if (overlay) {
      overlay.hidden = !active;
      if (active) {
        overlay.setAttribute('tabindex', '-1');
        overlay.focus?.({ preventScroll: true });
      } else {
        overlay.removeAttribute('tabindex');
      }
    }
    section.dataset.busy = active ? 'true' : 'false';
    if (active) section.setAttribute('aria-busy', 'true');
    else section.removeAttribute('aria-busy');
    if (root.document?.body) root.document.body.classList.toggle('ops-cafe-barrier-page-busy', active);
  }

  function init(section) {
    if (!section || section.dataset.opsCafeInitialized === 'true') return;
    const form = section.querySelector('[data-ops-cafe-barrier-form]');
    const variantSource = section.querySelector('[data-variant-source]');
    const modularSource = section.querySelector('[data-modular-variants]');
    const layoutInputs = Array.from(section.querySelectorAll('input[name="layout"]'));
    const replacementCount = section.querySelector('[data-replacement-count]');
    const panelsHost = section.querySelector('[data-panels]');
    const addButton = section.querySelector('[data-add-to-cart]');
    const copyButton = section.querySelector('[data-copy-config]');
    const status = section.querySelector('[data-status]');
    if (!form || !variantSource || !panelsHost || !addButton || !status) return;
    section.dataset.opsCafeInitialized = 'true';

    let busy = false;
    const labels = section.dataset;
    const beforeUnload = function (event) {
      if (!busy) return;
      event.preventDefault();
      event.returnValue = '';
    };
    const busyKeyGuard = function (event) {
      if (!busy || (event.key !== 'Tab' && event.key !== 'Escape')) return;
      event.preventDefault();
      section.querySelector('[data-busy]')?.focus?.({ preventScroll: true });
    };

    const rebuildPanels = function () {
      const layout = layoutFromForm(form);
      const count = panelCountForLayout(layout, Number(replacementCount?.value || 1));
      renderPanels(section, count);
      refresh();
    };

    const refresh = function () {
      const layout = layoutFromForm(form);
      const baseWrap = section.querySelector('[data-base-wrap]');
      const frameStep = section.querySelector('[data-frame-step]');
      const extensionWrap = section.querySelector('[data-extension-wrap]');
      const replacementWrap = section.querySelector('[data-replacement-wrap]');
      if (baseWrap) baseWrap.hidden = layout === 'replacement_graphics';
      if (frameStep) frameStep.hidden = layout === 'replacement_graphics';
      const finishSelect = section.querySelector('[data-finish]');
      if (finishSelect) finishSelect.required = layout !== 'replacement_graphics';
      if (extensionWrap) extensionWrap.hidden = layout !== 'extension';
      if (replacementWrap) replacementWrap.hidden = layout !== 'replacement_graphics';

      const draft = draftFromForm(section);
      const configuration = resolveCheckoutConfiguration(draft, variantSource, modularSource);
      const fingerprint = artworkFingerprint(draft);
      const guard = configuration && fingerprint ? cartAddGuardState(configuration, fingerprint) : null;
      const filesReady = artworkReady(draft);
      // A review-only Shopify template has deliberately blank worker_url.
      // Fail closed before enabling Add to Cart, rather than on click.
      const workerConfigured = Boolean(String(section.dataset.workerUrl || '').trim());
      const canCheckout = Boolean(configuration && workerConfigured && filesReady && guard?.canAdd && !busy);
      addButton.disabled = !canCheckout;

      const fixedSummary = section.querySelector('[data-fixed-summary]');
      const holdSummary = section.querySelector('[data-hold-summary]');
      const price = section.querySelector('[data-price]');
      if (fixedSummary) fixedSummary.hidden = !configuration;
      if (holdSummary) holdSummary.hidden = !draft || Boolean(configuration);
      if (price) price.textContent = configuration?.priceDisplay || '';
      const fixedNote = section.querySelector('[data-fixed-scope-note]');
      const modularNote = section.querySelector('[data-modular-scope-note]');
      const isModular = configuration?.kind === 'modular';
      if (fixedNote) fixedNote.hidden = !configuration || isModular;
      if (modularNote) modularNote.hidden = !isModular;
      const componentList = section.querySelector('[data-component-lines]');
      if (componentList) {
        componentList.textContent = '';
        componentList.hidden = !isModular;
        if (isModular) {
          for (const line of configuration.lines) {
            const row = root.document.createElement('li');
            const label = line.role === 'post' ? 'Post & base' :
              line.role === 'rail' ? 'Rail (' + (line.panelId || '') + ')' :
              line.role === 'clip' ? 'Spring clips (' + line.panelId + ')' :
              'Printed graphic (' + line.panelId + ')';
            row.textContent = line.quantity + ' × ' + label + ' — ' +
              new Intl.NumberFormat('en-GB',{style:'currency',currency:'GBP'}).format(
                line.quantity * line.pricePence / 100
              );
            componentList.appendChild(row);
          }
        }
      }

      const image = section.querySelector('[data-product-image]');
      const finish = section.querySelector('[data-finish]')?.value || 'black';
      const productImage = finish === 'chrome' ? labels.chromeImage : labels.blackImage;
      if (image && productImage) image.src = productImage;
      const productPhoto = section.querySelector('[data-product-photo]');
      if (productPhoto) productPhoto.hidden = layout !== 'straight';

      const diagram = section.querySelector('[data-layout-diagram]');
      const caption = section.querySelector('[data-layout-caption]');
      const diagramKey = layout === 'l_shaped' ? 'layoutLImage' : layout === 'c_shaped' ? 'layoutCImage' : layout === 'replacement_graphics' ? 'layoutReplacementImage' : layout === 'extension' ? 'layoutExtensionImage' : 'layoutStraightImage';
      const captionKey = layout === 'l_shaped' ? 'layoutLCaption' : layout === 'c_shaped' ? 'layoutCCaption' : layout === 'replacement_graphics' ? 'layoutReplacementCaption' : layout === 'extension' ? 'layoutExtensionCaption' : 'layoutStraightCaption';
      if (diagram && labels[diagramKey]) diagram.src = labels[diagramKey];
      if (caption) caption.textContent = labels[captionKey] || '';

      const counts = hardwareSummary(draft || buildDraftConfiguration({
        layout,
        finish: finish || 'black',
        base: section.querySelector('[data-base]')?.value || 'standard',
        replacementCount: Number(replacementCount?.value || 1),
        existingCompatiblePostConfirmed: layout !== 'extension',
        existingSystemReference: layout === 'extension' ? 'preview' : '',
        panels: collectPanels(panelsHost)
      }));
      const setText = function (selector, value) {
        const el = section.querySelector(selector);
        if (el) el.textContent = String(value);
      };
      setText('[data-post-count]', counts.posts);
      setText('[data-rail-count]', counts.rails);
      setText('[data-clip-count]', counts.clipPairs);
      setText('[data-graphic-count]', counts.graphics);

      Array.from(panelsHost.querySelectorAll('[data-panel-card]')).forEach(function (card) {
        syncPanelCard(card);
        const panelStatus = card.querySelector('[data-panel-status]');
        const sides = card.querySelector('[data-panel-sides]')?.value;
        const same = card.querySelector('[data-panel-same]')?.checked !== false;
        const front = card.querySelector('[data-panel-front]')?.files?.[0];
        const back = card.querySelector('[data-panel-back]')?.files?.[0];
        if (!front) panelStatus.textContent = labels.artworkRequiredMessage || '';
        else if (validateArtworkFile(front)) panelStatus.textContent = labels.invalidArtworkMessage || '';
        else if (sides === 'double' && !same && validateArtworkFile(back)) panelStatus.textContent = labels.backArtworkRequiredMessage || '';
        else panelStatus.textContent = labels.artworkReadyMessage || '';
      });

      if (busy) return;
      if (!draft) {
        status.textContent = layout === 'extension' && !section.querySelector('[data-extension-confirm]')?.checked
          ? (labels.extensionConfirmationMessage || '')
          : (labels.chooseMessage || '');
      } else if (!configuration) {
        status.textContent = labels.holdMessage || '';
      } else if (!workerConfigured) {
        status.textContent = 'Artwork uploads and checkout are not connected in this preview. You can still review and copy the configuration.';
      } else if (!filesReady) {
        status.textContent = labels.artworkRequiredMessage || '';
      } else if (!guard?.canAdd) {
        status.textContent = guard?.reason === 'attempted' ? (labels.cartMayContainMessage || '') : (labels.cartGuardUnavailableMessage || '');
      } else {
        status.textContent = labels.readyMessage || '';
      }
    };

    layoutInputs.forEach(function (input) { input.addEventListener('change', rebuildPanels); });
    replacementCount?.addEventListener('change', rebuildPanels);
    form.addEventListener('change', function (event) {
      if (event.target.matches('[data-replacement-count], input[name="layout"]')) return;
      refresh();
    });

    panelsHost.addEventListener('change', function (event) {
      const card = event.target.closest('[data-panel-card]');
      if (card) syncPanelCard(card);
      refresh();
    });

    copyButton?.addEventListener('click', async function () {
      const draft = draftFromForm(section);
      const summary = configurationSummary(draft);
      if (!summary) return;
      try {
        if (root.navigator?.clipboard?.writeText) {
          await root.navigator.clipboard.writeText(summary);
        } else {
          const helper = root.document?.createElement?.('textarea');
          if (!helper) throw new Error('Clipboard unavailable');
          helper.value = summary;
          helper.setAttribute('readonly', '');
          helper.style.position = 'fixed';
          helper.style.opacity = '0';
          root.document.body.appendChild(helper);
          helper.select();
          const copied = root.document.execCommand?.('copy');
          helper.remove();
          if (!copied) throw new Error('Clipboard unavailable');
        }
        status.textContent = labels.copiedMessage || 'Setup details copied.';
      } catch (error) {
        status.textContent = labels.copyFailedMessage || 'Could not copy the setup. Please note the panel details before contacting us.';
      }
    });

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      if (busy) return;
      const draft = draftFromForm(section);
      const configuration = resolveCheckoutConfiguration(draft, variantSource, modularSource);
      if (!draft || !configuration || !String(section.dataset.workerUrl || '').trim() || !artworkReady(draft)) {
        refresh();
        return;
      }
      const fingerprint = artworkFingerprint(draft);
      const guard = cartAddGuardState(configuration, fingerprint);
      if (!guard.canAdd) {
        refresh();
        return;
      }

      busy = true;
      root.addEventListener?.('beforeunload', beforeUnload);
      root.addEventListener?.('keydown', busyKeyGuard, true);
      Array.from(form.elements).forEach(function (control) { control.disabled = true; });
      addButton.setAttribute('aria-busy', 'true');
      setBusy(section, true, labels.preparingMessage || 'Preparing your order…');

      try {
        const updateStatus = function (message) {
          status.textContent = message;
          setBusy(section, true, message);
        };
        const snapshot = await createProductionSnapshot(draft, configuration, section.dataset.workerUrl, {
          artworkRequired: labels.artworkRequiredMessage,
          uploadingArtwork: labels.uploadingArtworkMessage,
          savingConfiguration: labels.savingConfigurationMessage,
          creatingPreview: labels.creatingPreviewMessage,
          finalizing: labels.finalizingMessage,
          finalizeFailed: labels.finalizeFailedMessage,
          setStatus: updateStatus
        });
        updateStatus(labels.addingMessage || 'Adding to cart…');
        await addCartItem(configuration, snapshot, draft, {
          cartMismatch: labels.cartMismatch,
          cartPersistenceError: labels.cartPersistenceError,
          cartMayContainItem: labels.cartMayContainMessage,
          cartGuardUnavailable: labels.cartGuardUnavailableMessage,
          beforeNavigate: function () {
            root.removeEventListener?.('beforeunload', beforeUnload);
            root.removeEventListener?.('keydown', busyKeyGuard, true);
          }
        });
      } catch (error) {
        busy = false;
        root.removeEventListener?.('beforeunload', beforeUnload);
        root.removeEventListener?.('keydown', busyKeyGuard, true);
        setBusy(section, false, '');
        addButton.removeAttribute('aria-busy');
        if (error?.cartMayContainItem) {
          status.textContent = labels.cartMayContainMessage || '';
          addButton.disabled = true;
          return;
        }
        if (error?.cartGuardUnavailable) {
          status.textContent = labels.cartGuardUnavailableMessage || '';
          addButton.disabled = true;
          return;
        }
        Array.from(form.elements).forEach(function (control) { control.disabled = false; });
        status.textContent = error?.message || labels.cartError || '';
        refresh();
      }
    });

    renderPanels(section, 1);
    refresh();
  }

  const api = Object.freeze({
    PRODUCT_IDS,
    EXPECTED,
    panelCountForLayout,
    buildDraftConfiguration,
    hardwareSummary,
    configurationSummary,
    resolveConfiguration,
    fixedCheckoutConfiguration,
    resolveCheckoutConfiguration,
    validateArtworkFile,
    artworkReady,
    artworkFingerprint,
    buildBomSnapshot,
    buildCartItem,
    createProductionSnapshot,
    addCartItem,
    canRetryCartAdd,
    cartAddGuardState,
    init
  });

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root.document) root.document.querySelectorAll('[data-ops-cafe-barrier-fixed]').forEach(init);
})(typeof window !== 'undefined' ? window : globalThis);
